import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { FinanceCue } from "@/components/common";
import { InstalmentCue } from "@/components/Instalments";

export function PromoBox() {
  const { promo, setPromo, quote } = useCart();
  const [code, setCode] = useState(promo);
  return (
    <div className="space-y-2">
      <div className="flex">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Promo code" className="flex-1 border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-neutral-900" data-testid="promo-code-input" />
        <button onClick={() => setPromo(code.trim())} className="btn-ink px-5 text-xs tracking-widest uppercase" data-testid="promo-apply-button">Apply</button>
      </div>
      {quote?.promo && <p className="text-xs text-emerald-700" data-testid="promo-success">{quote.promo.code} applied — {quote.promo.description}</p>}
      {quote?.promo_error && promo && <p className="text-xs text-red-800" data-testid="promo-error">{quote.promo_error}</p>}
    </div>
  );
}

export function Totals({ quote }: { quote: any }) {
  if (!quote) return null;
  return (
    <dl className="space-y-2 text-sm" data-testid="order-totals">
      <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid="totals-subtotal">{money(quote.subtotal)}</dd></div>
      {quote.discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Discount</dt><dd data-testid="totals-discount">−{money(quote.discount)}</dd></div>}
      {quote.rewards_applied > 0 && <div className="flex justify-between text-emerald-700"><dt>Somnio VIP rewards</dt><dd data-testid="totals-rewards">−{money(quote.rewards_applied)}</dd></div>}
      <div className="flex justify-between"><dt>{quote.shipping > 0 ? quote.shipping_label : "Insured shipping"}</dt><dd data-testid="totals-shipping">{quote.shipping > 0 ? money(quote.shipping) : "Complimentary"}</dd></div>
      <div className="flex justify-between font-display text-xl pt-3 border-t border-neutral-200"><dt>Total</dt><dd data-testid="totals-total">{money(quote.total)}</dd></div>
      {quote.rewards_earn > 0 && <p className="text-xs text-neutral-600 pt-1" data-testid="totals-rewards-earn">Earn <strong>{money(quote.rewards_earn)}</strong> in <Link to="/pages/rewards" className="underline">Somnio VIP</Link> rewards with this purchase</p>}
    </dl>
  );
}

export function CartDrawer() {
  const { items, setQty, open, setOpen, quote } = useCart();
  const nav = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[var(--c-bg)] w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <div className="px-6 pt-6 pb-4 border-b border-neutral-200">
          <SheetTitle className="font-display text-2xl font-light">Your Bag</SheetTitle>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {items.length === 0 && (
            <div className="text-center py-16" data-testid="cart-empty">
              <p className="font-display text-xl mb-6">Your bag is empty</p>
              <button onClick={() => { setOpen(false); nav("/collections/engagement-rings"); }} className="btn-ink px-8 py-3 text-xs tracking-widest uppercase">Discover Rings</button>
            </div>
          )}
          {items.map((i: any) => {
            const line = quote?.lines?.find((l: any) => l.product_id === i.product_id && l.metal === i.metal && JSON.stringify(l.builder) === JSON.stringify(i.builder || null) && (l.ring_size || null) === (i.ring_size || null));
            return (
              <div key={i.key} className="flex gap-4" data-testid="cart-line">
                <img src={i.image} alt={i.name} className="h-24 w-24 object-contain bg-white" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <Link to={`/products/${i.slug}`} onClick={() => setOpen(false)} className="font-display text-lg leading-tight">{i.name}</Link>
                    <button onClick={() => setQty(i.key, 0)} aria-label="Remove" data-testid="cart-line-remove"><X size={16} /></button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1">{[i.metal, i.ring_size && `Size ${i.ring_size}`, i.builder && `${i.builder.carat}ct ${i.builder.shape} · ${i.builder.color}/${i.builder.clarity}`, i.engraving && `“${i.engraving}”`].filter(Boolean).join(" · ")}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-neutral-300">
                      <button className="px-2 py-1" onClick={() => setQty(i.key, i.qty - 1)} aria-label="Decrease" data-testid="cart-qty-decrease"><Minus size={12} /></button>
                      <span className="px-3 text-sm" data-testid="cart-qty">{i.qty}</span>
                      <button className="px-2 py-1" onClick={() => setQty(i.key, Math.min(10, i.qty + 1))} aria-label="Increase" data-testid="cart-qty-increase"><Plus size={12} /></button>
                    </div>
                    <span className="text-sm">{money(line ? line.line_total : i.unit_price * i.qty)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {items.length > 0 && (
          <div className="border-t border-neutral-200 px-6 py-5 space-y-4 bg-white/60">
            <PromoBox />
            <Totals quote={quote} />
            {quote?.total > 0 && <div><InstalmentCue price={quote.total} testId="cart-instalment-cue" /><FinanceCue price={quote.total} testId="cart-finance-cue" /></div>}
            <button onClick={() => { setOpen(false); nav("/checkout"); }} className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="cart-checkout-button">Secure Checkout</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
